import axios, { type InternalAxiosRequestConfig } from 'axios';
import { parseFeatureLicenseError } from '../utils/featureAccessError';
import {
  getDevMockHostFromStorage,
  getDevTenantSlugFromStorage,
  isLocalDevHost,
} from './devTenantContext';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipFeatureUnavailableToast?: boolean;
  }
}

const TENANT_FORWARDED_HOST_HEADER = 'X-Forwarded-Host';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Função para obter token do sessionStorage
const getAuthToken = (): string | null => {
  const token = sessionStorage.getItem('@KnowInDoc:accessToken');
  return token;
};

// Função para obter token do customer
const getCustomerToken = (): string | null => {
  const token = sessionStorage.getItem('@KnowInDoc:customerToken');
  return token;
};

// Função para obter token do usuário
const getUserToken = (): string | null => {
  const token = sessionStorage.getItem('@KnowInDoc:userToken');
  return token;
};

// Função para obter customer_id e user_id do token de usuário
const getAuthHeaders = () => {
  const customerId = sessionStorage.getItem('@KnowInDoc:customer_id');
  const userId = sessionStorage.getItem('@KnowInDoc:user_id');
  
  return {
    'customer_id': customerId || '',
    'user_id': userId || ''
  };
};

function applyDevTenantToRequest(config: InternalAxiosRequestConfig) {
  if (!isLocalDevHost()) return;

  const mockHost = getDevMockHostFromStorage();
  const slug = getDevTenantSlugFromStorage();

  if (mockHost) {
    config.headers['X-Dev-Tenant-Host'] = mockHost;
    return;
  }

  if (slug) {
    const existing = config.params;
    if (existing instanceof URLSearchParams) {
      existing.set('tenantSlug', slug);
    } else if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
      config.params = { ...existing, tenantSlug: slug };
    } else {
      config.params = { tenantSlug: slug };
    }
    return;
  }

  config.headers['X-Dev-Tenant-Host'] = window.location.hostname;
}

function applyCrossOriginPublicHostHeader(config: InternalAxiosRequestConfig) {
  if (typeof window === 'undefined') return;

  const base = config.baseURL ?? api.defaults.baseURL;
  if (!base) return;

  let apiHostname: string;
  try {
    apiHostname = new URL(base, window.location.origin).hostname;
  } catch {
    return;
  }

  if (apiHostname === window.location.hostname) return;

  config.headers[TENANT_FORWARDED_HOST_HEADER] = window.location.hostname;
}

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    applyDevTenantToRequest(config);
    applyCrossOriginPublicHostHeader(config);

    // Não adicionar headers de autenticação para chamadas de autenticação
    if (config.url?.includes('/auth/customer') || 
        config.url?.includes('/auth/user') || 
        config.url?.includes('/auth/hybrid') ||
        config.url?.includes('/auth/validate') ||
        config.url?.includes('/auth/refresh') ||
        config.url?.includes('/token') ||
        config.url?.includes('/public/tenant') ||
        config.url?.includes('/public/csat')) {
      return config;
    }

    const isOptionalAuthPublicForm = config.url?.includes('/public/forms');

    const isAnonymousPublicSign =
      (config.url?.includes('/public/documents') ||
        config.url?.includes('/public/sign') ||
        config.url?.includes('/public/csat') ||
        isOptionalAuthPublicForm) &&
      !config.url?.includes('/public/sign/inbox');
    // Formulários privados usam o mesmo endpoint público com auth opcional:
    // se houver token, envia; se não, segue anônimo (AccessType.Public).
    if (isAnonymousPublicSign && !isOptionalAuthPublicForm)
    {
      return config;
    }

    const hybridToken = getAuthToken();
    const customerToken = getCustomerToken();
    const userToken = getUserToken();
    const authHeaders = getAuthHeaders();
    
    // Adicionar token híbrido como Authorization (principal)
    if (hybridToken) {
      config.headers.Authorization = `Bearer ${hybridToken}`;
    }
    
    // Adicionar token do customer como customer_token para compatibilidade
    if (customerToken) {
      config.headers['customer_token'] = customerToken;
    }
    
    // Adicionar token do usuário como user_token para compatibilidade
    if (userToken) {
      config.headers['user_token'] = userToken;
    }
    
    // Adicionar headers de contexto para auditoria (sempre)
    Object.assign(config.headers, authHeaders);
    
    // Se for FormData, não sobrescrever o Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const featureError = parseFeatureLicenseError(error.response?.data);
    if (
      error.response?.status === 403 &&
      featureError &&
      !originalRequest?.skipFeatureUnavailableToast
    )
    {
      window.dispatchEvent(
        new CustomEvent('feature-unavailable', {
          detail: { code: featureError.code, message: featureError.message },
        })
      );
    }

    const isAnonymousPublicSign =
      (originalRequest?.url?.includes('/public/documents') ||
        originalRequest?.url?.includes('/public/sign') ||
        originalRequest?.url?.includes('/public/forms') ||
        originalRequest?.url?.includes('/public/csat')) &&
      !originalRequest?.url?.includes('/public/sign/inbox');
    if (isAnonymousPublicSign)
    {
      return Promise.reject(error);
    }
    
    // Se recebeu 401 e não é uma tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Tentar renovar o token
        const refreshToken = sessionStorage.getItem('@KnowInDoc:refreshToken');
        if (refreshToken) {
          const response = await api.post('/auth/refresh', {
            refreshToken
          });
          
          if (response.data.token) {
            // Salvar novo token
            sessionStorage.setItem('@KnowInDoc:accessToken', response.data.token);
            sessionStorage.setItem('@KnowInDoc:refreshToken', response.data.refreshToken || '');
            
            // Reexecutar requisição original
            originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
            return api(originalRequest);
          }
        }
      } catch {
        sessionStorage.clear();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Função para verificar se os tokens estão válidos
export function checkAuthStatus() {
  const tokens = {
    hybridToken: getAuthToken(),
    customerToken: getCustomerToken(),
    userToken: getUserToken(),
    authHeaders: getAuthHeaders()
  };
  
  return tokens;
}

// Função para debug do localStorage/sessionStorage
export function debugLocalStorage() {
  // Função de debug removida em produção
} 